import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Logger } from '../../../core/services/logger.service';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CitiesService } from '../../../shared/services/cities.service';
import { DropdownComponent } from '../dropdown/dropdown.component';

@Component({
  selector: 'app-city-dropdown',
  templateUrl: './city-dropdown.component.html',
  styleUrl: './city-dropdown.component.scss',
})
export class CityDropdownComponent
  implements OnInit, ControlValueAccessor
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = new Logger(CityDropdownComponent.name);

  @Input() label?: string | undefined = undefined;
  @Input() placeholder = 'Sélectionnez votre ville';
  @Input() errorMessages: string[] = [];
  @Input() disabled = false;

  @Output() citySelected = new EventEmitter<string>();

  @ViewChild(DropdownComponent) dropdown!: DropdownComponent;

  public hasBeenUpdated = false;

  public citiesOptions: { label: string; value: string }[] = [];
  public filteredCitiesOptions: { label: string; value: string }[] = [];

  protected _value = '';

  constructor(
    private citiesService: CitiesService,
    @Self() @Optional() public control: NgControl
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this._value && this._value !== '') {
      this.filteredCitiesOptions = [
        {
          value: this._value,
          label: this._value,
        },
      ];
    }
    // S'abonner aux résultats de recherche avec debounce
    this.citiesService
      .searchCitiesWithDebounce()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: searchResults => {
          this.filteredCitiesOptions = searchResults.map(city => ({
            value: `${city.city_code || city.cityCode}-${city.zip_code || city.zipCode}`,
            label: `${city.label} (${city.zip_code || city.zipCode})`,
          }));
        },
        error: error => {
          this.logger.error('Error in city search subscription', error);
          this.filteredCitiesOptions = [];
        },
      });
  }

  onFilterCities(event: any): void {
    const query = event.filter || '';

    if (query.length < 2) {
      this.filteredCitiesOptions = [];
      return;
    }

    // Déclencher la recherche avec debounce
    this.citiesService.search(query);
  }

  onCityChange(value: string) {
    if (!value) return;
    this.citySelected.emit(value);
  }

  // ControlValueAccessor implementation
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  async writeValue(value: string): Promise<void> {
    this._value = value;
    if (value) {
      try {
        // Check if value is already in filteredCitiesOptions
        const exists = this.filteredCitiesOptions.some(
          opt => opt.value === value
        );
        if (!exists) {
          // Parse cityCode and zipCode from value
          const [cityCode, zipCode] = value.split('-');
          if (cityCode && zipCode) {
            const city = await this.citiesService.findCityByCodeAndZipCode(
              cityCode,
              zipCode
            );
            if (city) {
              const option = {
                value: `${city.city_code || city.cityCode}-${city.zip_code || city.zipCode}`,
                label: `${city.label} (${city.zip_code || city.zipCode})`,
              };
              this.filteredCitiesOptions = [
                option,
                ...this.filteredCitiesOptions,
              ];
            }
          }
        }
      } catch (error) {
        this.logger.error('Error in writeValue', error);
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    if (value !== this._value) {
      this._value = value;
      this.onChange(value);
      this.onCityChange(value);
      this.hasBeenUpdated = true;
    }
    this.onTouched();
  }

  public openDropdown(): void {
    if (this.dropdown) {
      this.dropdown.openDropdown();
    }
  }
}
